import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { isMobile } from 'react-device-detect';
import { Formik, ErrorMessage } from "formik";
import { Dialog, DialogContent, DialogTitle, IconButton, Box, Typography } from '@mui/material';
import { RiCloseLine } from "react-icons/ri";
import { callErrorApi } from '../errorHandle/callErrorApi';

function SignIn(props) {
  const navigate = useNavigate();
  
  // Agreement popup state
  const [showAgreementPopup, setShowAgreementPopup] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [userType, setUserType] = useState(''); // 'customer' or 'chef'
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // SEO setup
  useEffect(() => {
    const title = props.customer 
      ? "Signin | CHEF REPUBLIC" 
      : "Signin | CHEF | CHEF REPUBLIC";
    const desc = props.customer
      ? "Customer must signin or even Continue as guest in order to browse Private Chefs and various Cuisines/Diets"
      : "Signin your Personal Chef Account with Chef Republic to meal prep for our customers";

    document.title = title;
    document.querySelector("meta[name='description']").content = desc;
    document.querySelector("meta[property='og:title']").content = title;
    document.querySelector("meta[property='og:description']").content = desc;
    document.querySelector("meta[property='twitter:title']").content = title;
    document.querySelector("meta[property='twitter:description']").content = desc;
  }, [props.customer]);

  // const handleLoginError = (data, values, setFieldError) => {
  //   callErrorApi(`response_error: ${process.env.REACT_APP_BASE_URL}auth/login`, values.email, data);
  //   debugger
  //   if (data.data?.otp_verify === "0") {
  //     navigate(
  //       props.customer ? "/sign-up/verify-otp" : "/chef/sign-up/verify-otp",
  //       { state: { email: data.data.email || "" } }
  //     );
  //     return;
  //   }



  //   const errorMessage = data.messages?.error?.toLowerCase() || "";
  //   if (errorMessage.includes("email")) setFieldError('email', data.messages.error);
  //   if (errorMessage.includes("password")) setFieldError('password', data.messages.error);
  // };

  const handleAgreementUpdate = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}user/update_agreement`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: localStorage.getItem("logged_user_id"),
          agreement: "yes"
        }),
      });
      
      const data = await response.json();
      if (data.status) {
        setShowAgreementPopup(false);
        setAgreementChecked(false);
        // Navigate to the pending destination
        if (pendingNavigation) {
          navigate(pendingNavigation);
          setPendingNavigation(null);
        }
      } else {
        console.error("Failed to update agreement:", data.message);
      }
    } catch (error) {
      console.error("Error updating agreement:", error);
    }
  };

  const handleLoginError = (data, values, setFieldError) => {
    callErrorApi(`response_error: ${process.env.REACT_APP_BASE_URL}auth/login`, values.email, data);
  
    if (data.data?.otp_verify === "0") {
      navigate(
        props.customer ? "/sign-up/verify-otp" : "/chef/sign-up/verify-otp",
        { state: { email: data.data.email || "" } }
      );
      return;
    }
  
    const errorMessage = data.messages?.error?.toLowerCase() || "";
    if (errorMessage.includes("email")) setFieldError('email', data.messages.error);
    if (errorMessage.includes("password")) setFieldError('password', data.messages.error);
    if (errorMessage.includes("invalid credentials")) setFieldError('password', "Incorrect password. Please try again.");
  };
  

 const handleCustomerLogin = async (data, values) => {
  const { chef_status, card_added, user_id, token, postal_code, user_image, plan_status } = data.data;

  // Set common storage items
  localStorage.setItem("token", token);
  localStorage.setItem("card_added", card_added);
  localStorage.setItem("user_image", user_image);
  localStorage.setItem("postalCode", localStorage.getItem("postalCode") || postal_code);

  // Fetch customer details
  await checksubcription(postal_code);
  const userResponse = await fetchUserDetails("user/loggedin_customer_detail", token);
  const customer = userResponse.data[0];

  // Update storage
  localStorage.setItem("owner", values.email);
  localStorage.setItem("logged_user_id", customer.customer_id);
  localStorage.setItem("plan_status", customer.plan_status || plan_status);

  // Check agreement status
  if (customer.agreement === "no") {
    setUserType('customer');
    setShowAgreementPopup(true);
    
    // ✅ Simplified pending navigation
    const hasSubscription = (customer.plan_status === "true" || plan_status === "true");
    const navigationPath = hasSubscription ? "/subscription-summary" : "/chef-list";
    setPendingNavigation(navigationPath);
    return; // Wait for agreement
  }

  // ✅ Final navigation logic
  const hasSubscription = (customer.plan_status === "true" || plan_status === "true");
  
  if (hasSubscription) {
    navigate("/subscription-summary");
  } else {
    navigate("/chef-list");
  }
};

  const handleChefLogin = async (data, values) => {
    const { token, postal_code, user_image, bank_added, profile_updated } = data.data;

    if (!token) {
      handleChefSetup(bank_added, profile_updated, values);
      return;
    }

    const chefResponse = await fetchUserDetails("user/loggedin_chef_detail", token);
    const chef = chefResponse.data[0];

    localStorage.setItem("owner", values.email);
    localStorage.setItem("token", token);
    localStorage.setItem("user_image", user_image);
    localStorage.setItem("postalCode", postal_code);
    localStorage.setItem("logged_user_id", chef.chef_id);
    localStorage.setItem("chef_id", chef.chef_id);

    // Check agreement status
    if (chef.agreement === "no") {
      setUserType('chef');
      setShowAgreementPopup(true);
      setPendingNavigation("/chef/order-list");
      return; // Don't navigate yet, wait for agreement
    }

    navigate("/chef/order-list");
  };

  const fetchUserDetails = async (endpoint, token) => {
    const user_id = localStorage.getItem("logged_user_id");
    const email = localStorage.getItem("owner");
    
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}${endpoint}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        user_id: user_id || "",
        email: email || ""
      }),
    });
    return response.json();
  };

  const handleChefSetup = (bankAdded, profileUpdated, values) => {
    if (!bankAdded) {
      navigate("/chef/add-payment-details", { state: { email: values.email } });
    } else if (!profileUpdated) {
      navigate("/chef/complete-profile", { state: { email: values.email } });
    } else {
      navigate("/under-review");
    }
  };

  const checksubcription = async (postalCode) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}subscription/check_current_plan`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          user_id: localStorage.getItem("logged_user_id"),
          postal_code: localStorage.getItem("postalCode") || postalCode
        })
      });

      const data = await response.json();
      if (data.status && !data.chef_status && localStorage.getItem("plan_id")) {
        await setUserSubscription(localStorage.getItem("plan_id"));
      }
    } catch (error) {
      console.error('Subscription check failed:', error);
    }
  };

  const setUserSubscription = async (planId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}subscription/add_subscription_plan`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: planId,
          user_id: localStorage.getItem("logged_user_id")
        })
      });
      
      const data = await response.json();
      if (data.status) localStorage.setItem("plan_status", "true");
    } catch (error) {
      console.error('Subscription update failed:', error);
    }
  };

  return (
    <div className="row m-0 p-0">
      {/* Left Column */}
      <div
        className="col-md-7"
        style={{
          background: `linear-gradient(to left, rgba(255, 255, 255, 1) 0%,  rgba(255, 255, 255, 0.8) 10%, rgba(255, 255, 255, 0.2) 18%, rgba(255, 255, 255, 0) 25%, rgba(255, 255, 255, 0) 100%), url(${props.image}) no-repeat`,
          height: "100vh",
          backgroundSize: "auto 100%",
          display: isMobile ? "none" : "block",
        }}
      />

      {/* Right Column */}
      <div className="col-md-5 d-flex align-items-center pt-5 justify-content-center flex-column">
        <div className="d-flex justify-content-between" style={{ width: isMobile ? "90%" : "70%" }}>
          <h2>
            <span style={{ color: "#FF5E41", fontWeight: 700 }}>Sign In</span>
          </h2>
          <h2>
            <Link
              to={props.customer ? "/sign-up" : "/chef/sign-up"}
              style={{
                textDecoration: "none",
                color: "rgba(0, 0, 0, .5)",
                fontWeight: 700
              }}
            >
              Sign Up
            </Link>
          </h2>
        </div>

        <div className="d-flex justify-content-start mt-2 mb-3" style={{ width: isMobile ? "90%" : "70%" }}>
          <h4 style={{ fontSize: "16px", width: "320px" }}>
            {props.customer ? "to hire a private chef on demand" : "to get more clients"}
          </h4>
        </div>

        {/* Formik Form */}
        <Formik
          initialValues={{ email: '', password: '' }}
          validate={values => {
            const errors = {};
            if (!values.email) {
              errors.email = 'Email is Required';
            } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
              errors.email = 'Invalid email address';
            }
            if (!values.password) {
              errors.password = 'Password is Required';
            } else if (values.password.length < 8) {
              errors.password = 'Password must be of 8 Character';
            }
            return errors;
          }}
          onSubmit={async (values, { setSubmitting, setFieldError }) => {
            try {
              const response = await fetch(`${process.env.REACT_APP_BASE_URL}auth/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(values),
              });

              const data = await response.json().catch(() => ({}));
              
              if (data.status === 400) {
                handleLoginError(data, values, setFieldError);
              }

              if (!data?.status ) {
                handleLoginError(data, values, setFieldError);
                return;
              }

              // Common storage items
              localStorage.setItem("role", data.data.role);
              localStorage.setItem("chef_status", data.data.chef_status);
              localStorage.setItem("logged_user_id", data.data.user_id);

              // Role handling
              data.data.role === "1" 
                ? await handleCustomerLogin(data, values)
                : await handleChefLogin(data, values);

            } catch (error) {
              callErrorApi(`catch_error: ${process.env.REACT_APP_BASE_URL}auth/login`, values.email, error);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <form 
              style={{ width: isMobile ? "90%" : "70%", paddingBottom: "60px" }}
              onSubmit={handleSubmit}
            >
              {/* Form Fields */}
              <div className="mb-3">
                <input
                  placeholder="Email ID"
                  type="email"
                  className={`form-control shadow-none ${errors.email ? 'invalid' : ''}`}
                  name="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                />
                <ErrorMessage name="email">{msg => <h6 className="error-msg">{msg}</h6>}</ErrorMessage>
              </div>

              <div className="mb-3">
                <input
                  placeholder="Password"
                  type="password"
                  className={`form-control shadow-none ${errors.password ? 'invalid' : ''}`}
                  name="password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.password}
                />
                {/* <ErrorMessage name="password">{msg => <h6 className="error-msg">{msg}</h6>}</ErrorMessage> */}
                <ErrorMessage name="password">
    {msg => <h6 className="error-msg">{msg}</h6>}
  </ErrorMessage>
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  className="m-2 btn-orange w-100"
                  disabled={isSubmitting}
                >
                  {isSubmitting && <span className="spinner-border spinner-border-sm me-2"></span>}
                  Sign In
                </button>
              </div>

              <h2 className="mb-4 text-center">
                <Link
                  to={props.customer ? "/forgot-password" : "/chef/forgot-password"}
                  style={{ textDecoration: "none", color: "#FF5E41" }}
                >
                  <span style={{ fontSize: "16px" }}>Forgot Password?</span>
                </Link>
              </h2>
            </form>
          )}
        </Formik>
      </div>
      
      {/* Agreement Popup */}
      <Dialog
        open={showAgreementPopup}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "15px" }
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Terms & Conditions Agreement
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setShowAgreementPopup(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <RiCloseLine />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Please agree to our Terms & Conditions to continue using Chef Republic:
            </Typography>
            
            {userType === 'customer' ? (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <input
                  type="checkbox"
                  id="agreement_check_customer"
                  checked={agreementChecked}
                  onChange={(e) => setAgreementChecked(e.target.checked)}
                  style={{ marginTop: '4px', marginRight: '8px' }}
                />
                <label htmlFor="agreement_check_customer" style={{ fontSize: "14px", cursor: "pointer" }}>
                  I agree to book chefs through Chef Republic for any services introduced on the platform as outlined in the{" "}
                  <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" style={{ color: "#0524d1" }}>
                    Terms & Conditions
                  </a>.
                </label>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <input
                  type="checkbox"
                  id="agreement_check_chef"
                  checked={agreementChecked}
                  onChange={(e) => setAgreementChecked(e.target.checked)}
                  style={{ marginTop: '4px', marginRight: '8px' }}
                />
                <label htmlFor="agreement_check_chef" style={{ fontSize: "14px", cursor: "pointer" }}>
                  I agree to accept and fulfill bookings only through Chef Republic for any clients introduced on the platform as outlined in the{" "}
                  <a href="/chef/terms-and-conditions" target="_blank" rel="noopener noreferrer" style={{ color: "#0524d1" }}>
                    Terms & Conditions
                  </a>.
                </label>
              </Box>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <button
              className="btn-orange"
              onClick={handleAgreementUpdate}
              disabled={!agreementChecked}
              style={{
                width: "100%",
                padding: "10px 20px",
                borderRadius: "25px",
                border: "none",
                cursor: agreementChecked ? "pointer" : "not-allowed",
                opacity: agreementChecked ? 1 : 0.6
              }}
            >
              Continue
            </button>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SignIn;


// import React, { useEffect } from 'react';
// import { Link, useNavigate } from "react-router-dom";
// import { isMobile } from 'react-device-detect';
// import { Formik, ErrorMessage } from "formik";
// import { callErrorApi } from '../errorHandle/callErrorApi';

// function SignIn(props) {
//   const navigate = useNavigate();

//   // 🧠 Guest Redirect Logic: Trigger early before form renders
//   useEffect(() => {
//     const isGuestUser = localStorage.getItem("is_guest_user") === "1" || localStorage.getItem("is_guest_user") === "true";
//     const noSubscription = localStorage.getItem("no_subscription") === "1" || localStorage.getItem("no_subscription") === "true";
//     const bookingDetails = localStorage.getItem("booking_details");

//     if (isGuestUser && noSubscription && bookingDetails) {
//       try {
//         const parsedDetails = JSON.parse(bookingDetails);
//         navigate("/booking-summary", { state: parsedDetails });
//       } catch (err) {
//         console.error("Invalid booking_details format:", err);
//       }
//     }
//   }, [navigate]);

//   const handleLoginError = (data, values, setFieldError) => {
//     callErrorApi(`response_error: ${process.env.REACT_APP_BASE_URL}auth/login`, values.email, data);

//     if (data.data?.otp_verify === "0") {
//       navigate(
//         props.customer ? "/sign-up/verify-otp" : "/chef/sign-up/verify-otp",
//         { state: { email: data.data.email || "" } }
//       );
//       return;
//     }

//     const errorMessage = data.messages?.error?.toLowerCase() || "";
//     if (errorMessage.includes("email")) setFieldError('email', data.messages.error);
//     if (errorMessage.includes("password")) setFieldError('password', data.messages.error);
//     if (errorMessage.includes("invalid credentials")) setFieldError('password', "Incorrect password. Please try again.");
//   };

//   const handleCustomerLogin = async (data, values) => {
//     const { chef_status, card_added, user_id, token, postal_code, user_image, plan_status } = data.data;
//     localStorage.setItem("token", token);
//     localStorage.setItem("card_added", card_added);
//     localStorage.setItem("user_image", user_image);
//     localStorage.setItem("postalCode", localStorage.getItem("postalCode") || postal_code);

//     await checksubcription(postal_code);
//     const userResponse = await fetchUserDetails("user/loggedin_customer_detail", token);
//     const customer = userResponse.data[0];

//     localStorage.setItem("owner", values.email);
//     localStorage.setItem("logged_user_id", customer.customer_id);
//     localStorage.setItem("plan_status", customer.plan_status || plan_status);

//     // ✅ Guest Flow Handling After Login
//     const isGuestUser = localStorage.getItem("is_guest_user") === "1" || localStorage.getItem("is_guest_user") === "true";
//     const noSubscription = localStorage.getItem("no_subscription") === "1" || localStorage.getItem("no_subscription") === "true";
//     const bookingDetails = localStorage.getItem("booking_details");

//     if (isGuestUser && bookingDetails) {
//   const hasPlan = plan_status === "true" || customer.plan_status === "true";

//   if (hasPlan) {
//     return navigate("/subscription-summary");
//   } else {
//     return navigate("/booking-summary", { state: JSON.parse(bookingDetails) });
//   }
// }

// if (customer.plan_status === "true" || plan_status === "true") {
//   return navigate("/subscription-summary");
// }


//     // Normal flow
//     if (chef_status) return navigate("/subscription-summary");

//     const planStatus = localStorage.getItem("plan_status");
//     const chefId = localStorage.getItem("chef_id");
//     const redirectTo = localStorage.getItem("redirect_to");

//     navigate(
//       planStatus === 'true' ? (chefId ? "/subscription-detail" : "/chef-list") :
//       planStatus === 'false' ? (redirectTo || "/subscription-plans") :
//       redirectTo || "/chef-list"
//     );
//   };

//   const handleChefLogin = async (data, values) => {
//     const { token, postal_code, user_image, bank_added, profile_updated } = data.data;

//     if (!token) {
//       handleChefSetup(bank_added, profile_updated, values);
//       return;
//     }

//     const chefResponse = await fetchUserDetails("user/loggedin_chef_detail", token);
//     const chef = chefResponse.data[0];

//     localStorage.setItem("owner", values.email);
//     localStorage.setItem("token", token);
//     localStorage.setItem("user_image", user_image);
//     localStorage.setItem("postalCode", postal_code);
//     localStorage.setItem("logged_user_id", chef.chef_id);
//     localStorage.setItem("chef_id", chef.chef_id);

//     navigate("/chef/order-list");
//   };

//   const fetchUserDetails = async (endpoint, token) => {
//     const response = await fetch(`${process.env.REACT_APP_BASE_URL}${endpoint}`, {
//       method: "POST",
//       headers: {
//         Authorization: `Bearer ${token}`,
//         "Content-Type": "application/json",
//       },
//     });
//     return response.json();
//   };

//   const handleChefSetup = (bankAdded, profileUpdated, values) => {
//     if (!bankAdded) {
//       navigate("/chef/add-payment-details", { state: { email: values.email } });
//     } else if (!profileUpdated) {
//       navigate("/chef/complete-profile", { state: { email: values.email } });
//     } else {
//       navigate("/under-review");
//     }
//   };

//   const checksubcription = async (postalCode) => {
//     try {
//       const response = await fetch(`${process.env.REACT_APP_BASE_URL}subscription/check_current_plan`, {
//         method: 'POST',
//         headers: {
//           Authorization: `Bearer ${localStorage.getItem("token")}`,
//           "Content-Type": "application/json"
//         },
//         body: JSON.stringify({
//           user_id: localStorage.getItem("logged_user_id"),
//           postal_code: localStorage.getItem("postalCode") || postalCode
//         })
//       });

//       const data = await response.json();
//       if (data.status && !data.chef_status && localStorage.getItem("plan_id")) {
//         await setUserSubscription(localStorage.getItem("plan_id"));
//       }
//     } catch (error) {
//       console.error('Subscription check failed:', error);
//     }
//   };

//   const setUserSubscription = async (planId) => {
//     try {
//       const response = await fetch(`${process.env.REACT_APP_BASE_URL}subscription/add_subscription_plan`, {
//         method: 'POST',
//         headers: {
//           Authorization: `Bearer ${localStorage.getItem("token")}`,
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify({
//           plan_id: planId,
//           user_id: localStorage.getItem("logged_user_id")
//         })
//       });

//       const data = await response.json();
//       if (data.status) localStorage.setItem("plan_status", "true");
//     } catch (error) {
//       console.error('Subscription update failed:', error);
//     }
//   };


//   return (
//     <div className="row m-0 p-0">
//       {/* Left Column */}
//       <div
//         className="col-md-7"
//         style={{
//           background: `linear-gradient(to left, rgba(255, 255, 255, 1) 0%,  rgba(255, 255, 255, 0.8) 10%, rgba(255, 255, 255, 0.2) 18%, rgba(255, 255, 255, 0) 25%, rgba(255, 255, 255, 0) 100%), url(${props.image}) no-repeat`,
//           height: "100vh",
//           backgroundSize: "auto 100%",
//           display: isMobile ? "none" : "block",
//         }}
//       />

//       {/* Right Column */}
//       <div className="col-md-5 d-flex align-items-center pt-5 justify-content-center flex-column">
//         <div className="d-flex justify-content-between" style={{ width: isMobile ? "90%" : "70%" }}>
//           <h2>
//             <span style={{ color: "#FF5E41", fontWeight: 700 }}>Sign In</span>
//           </h2>
//           <h2>
//             <Link
//               to={props.customer ? "/sign-up" : "/chef/sign-up"}
//               style={{
//                 textDecoration: "none",
//                 color: "rgba(0, 0, 0, .5)",
//                 fontWeight: 700
//               }}
//             >
//               Sign Up
//             </Link>
//           </h2>
//         </div>

//         <div className="d-flex justify-content-start mt-2 mb-3" style={{ width: isMobile ? "90%" : "70%" }}>
//           <h4 style={{ fontSize: "16px", width: "320px" }}>
//             {props.customer ? "to hire a private chef on demand" : "to get more clients"}
//           </h4>
//         </div>

//         {/* Formik Form */}
//         <Formik
//           initialValues={{ email: '', password: '' }}
//           validate={values => {
//             const errors = {};
//             if (!values.email) {
//               errors.email = 'Email is Required';
//             } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
//               errors.email = 'Invalid email address';
//             }
//             if (!values.password) {
//               errors.password = 'Password is Required';
//             } else if (values.password.length < 8) {
//               errors.password = 'Password must be of 8 Character';
//             }
//             return errors;
//           }}
//           onSubmit={async (values, { setSubmitting, setFieldError }) => {
//             try {
//               const response = await fetch(`${process.env.REACT_APP_BASE_URL}auth/login`, {
//                 method: "POST",
//                 headers: { "Content-Type": "application/json" },
//                 body: JSON.stringify(values),
//               });

//               const data = await response.json().catch(() => ({}));
              
//               if (data.status === 404) {
//                 handleLoginError(data, values, setFieldError);
//               }
//               if (data.status === 400) {
//                 handleLoginError(data, values, setFieldError);
//               }

//               if (!data?.status ) {
//                 handleLoginError(data, values, setFieldError);
//                 return;
//               }

//               // Common storage items
//               localStorage.setItem("role", data.data.role);
//               localStorage.setItem("chef_status", data.data.chef_status);
//               localStorage.setItem("logged_user_id", data.data.user_id);

//               // Role handling
//               data.data.role === "1" 
//                 ? await handleCustomerLogin(data, values)
//                 : await handleChefLogin(data, values);

//             } catch (error) {
//               callErrorApi(`catch_error: ${process.env.REACT_APP_BASE_URL}auth/login`, values.email, error);
//             } finally {
//               setSubmitting(false);
//             }
//           }}
//         >
//           {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
//             <form 
//               style={{ width: isMobile ? "90%" : "70%", paddingBottom: "60px" }}
//               onSubmit={handleSubmit}
//             >
//               {/* Form Fields */}
//               <div className="mb-3">
//                 <input
//                   placeholder="Email ID"
//                   type="email"
//                   className={`form-control shadow-none ${errors.email ? 'invalid' : ''}`}
//                   name="email"
//                   onChange={handleChange}
//                   onBlur={handleBlur}
//                   value={values.email}
//                 />
//                 <ErrorMessage name="email">{msg => <h6 className="error-msg">{msg}</h6>}</ErrorMessage>
//               </div>

//               <div className="mb-3">
//                 <input
//                   placeholder="Password"
//                   type="password"
//                   className={`form-control shadow-none ${errors.password ? 'invalid' : ''}`}
//                   name="password"
//                   onChange={handleChange}
//                   onBlur={handleBlur}
//                   value={values.password}
//                 />
//                 {/* <ErrorMessage name="password">{msg => <h6 className="error-msg">{msg}</h6>}</ErrorMessage> */}
//                 <ErrorMessage name="password">
//     {msg => <h6 className="error-msg">{msg}</h6>}
//   </ErrorMessage>
//               </div>

//               <div className="text-center">
//                 <button
//                   type="submit"
//                   className="m-2 btn-orange w-100"
//                   disabled={isSubmitting}
//                 >
//                   {isSubmitting && <span className="spinner-border spinner-border-sm me-2"></span>}
//                   Sign In
//                 </button>
//               </div>

//               <h2 className="mb-4 text-center">
//                 <Link
//                   to={props.customer ? "/forgot-password" : "/chef/forgot-password"}
//                   style={{ textDecoration: "none", color: "#FF5E41" }}
//                 >
//                   <span style={{ fontSize: "16px" }}>Forgot Password?</span>
//                 </Link>
//               </h2>
//             </form>
//           )}
//         </Formik>
//       </div>
//     </div>
//   );
// }

// export default SignIn;